/**
 * Connection to the Content Hub APIs, shared by the server (SSR, CPA proxy, middlewares), the content client and
 * the DevToolkit: delivery (CDA) and preview (CPA).
 * @description
 * The browser never talks to the CPA nor holds its token: it calls {@link CPA_PROXY_ROUTE}, the server adds the
 * token and picks host, API version, space and environment. In non-production instances the DevToolkit can point
 * the preview to another Content Hub (e.g. the development CMS) for the session: it posts the values to
 * {@link CPA_CONNECTION_ROUTE}, which validates them and stores them in the httpOnly cookie
 * {@link CPA_CONNECTION_COOKIE}, plus a copy without the token ({@link CPA_CONNECTION_INFO_COOKIE}) for the UI.
 * Inside the CMS live preview iframe the CMS hands its own CPA and a short-lived token in the URL: the server
 * applies them as the same override (`server/cms/contentHubConnection.ts`).
 * No path aliases here: also imported by the Express server and the request processor (keep it dependency-free).
 */

/** Proxy of the CPA: `${CPA_PROXY_ROUTE}/entries?...`. */
export const CPA_PROXY_ROUTE = '/mobify/custom/proxy/cpa';

/** POST sets the connection override, DELETE removes it. */
export const CPA_CONNECTION_ROUTE = '/mobify/custom/cms-preview/connection';

/** POST from the CMS (CORS): the pages that render an entry, see `server/middlewares/previewLocations.ts`. */
export const PREVIEW_LOCATIONS_ROUTE = '/mobify/custom/cms-preview/locations';

/** httpOnly, with the token: read by the server only. */
export const CPA_CONNECTION_COOKIE = 'cmsCpaConn';

/** Same values without the token, for the DevToolkit and the inspector "Edit" links. Never read by the server. */
export const CPA_CONNECTION_INFO_COOKIE = 'cmsCpaConnInfo';

/** CMS mode, `preview` or `live`: same key as the brands' `CMS_MODE_STORAGE_KEY`. */
export const CMS_MODE_COOKIE = 'cmsMode';

/** `scheduled_at` of the CPA requests (DevToolkit "effective date"). */
export const CMS_SCHEDULED_AT_COOKIE = 'cmsScheduledAt';

/** CMS opened by the inspector "Edit" button when the page is not inside the CMS iframe. */
export const DEFAULT_CMS_URL = 'https://contenthub.tlgtool.com';

/**
 * Origins the CMS runs on (production, preview, legacy workers.dev, local Vite dev server): they open the
 * storefront in live preview and may embed it.
 */
export const CMS_ORIGINS = [
	DEFAULT_CMS_URL,
	'https://contenthub-preview.tlgtool.com',
	'https://tlg-ch-web.accounts-ops.workers.dev',
	'https://tlg-ch-web-preview.accounts-ops.workers.dev',
	'http://localhost:5173',
];

/**
 * Request class of a request sent from a CMS page: `CMS_ORIGIN-{index in CMS_ORIGINS}`. Set by the request
 * processor at the edge, which sees `Origin` and `Referer`: MRT forwards neither of them to the server.
 */
const CMS_ORIGIN_REQUEST_CLASS_PREFIX = 'CMS_ORIGIN-';

/**
 * Origin of the page that sent a request: its `Origin`, else the origin of its `Referer` (the browser sends it on
 * CORS preflights too, reduced to the origin by the default referrer policy). A browser page can set neither.
 * @param {string | undefined} origin - `Origin` header
 * @param {string | undefined} referer - `Referer` header
 * @returns {string | undefined}
 */
export const getRequestingOrigin = (origin: string | undefined, referer: string | undefined): string | undefined => {
	if (origin) {
		return origin;
	}

	try {
		return new URL(referer).origin;
	} catch {
		return undefined;
	}
};

/**
 * Request class of the CMS origin that sent a request.
 * @param {string | undefined} origin - Origin of the page (`Origin`, else the origin of `Referer`)
 * @returns {string | null} The class, or `null` for any other origin
 */
export const getCmsOriginRequestClass = (origin: string | undefined): string | null => {
	const index = origin ? CMS_ORIGINS.indexOf(origin) : -1;

	return index === -1 ? null : `${CMS_ORIGIN_REQUEST_CLASS_PREFIX}${index}`;
};

/**
 * CMS origin of a request from its request classes (see {@link getCmsOriginRequestClass}).
 * @param {string[]} requestClasses - Request classes
 * @returns {string | undefined}
 */
export const getCmsOriginFromRequestClasses = (requestClasses: string[] = []): string | undefined => {
	const requestClass = requestClasses.find((c) => c.startsWith(CMS_ORIGIN_REQUEST_CLASS_PREFIX));

	return requestClass ? CMS_ORIGINS[Number(requestClass.slice(CMS_ORIGIN_REQUEST_CLASS_PREFIX.length))] : undefined;
};

export const DEFAULT_CDA_HOST = 'cdn.contentsvc.com';
export const DEFAULT_CPA_HOST = 'preview.contentsvc.com';

/** Values the DevToolkit can override; empty ones keep the default of the instance. */
export interface ICpaConnectionOverride {
	host?: string;
	spaceId?: string;
	environmentId?: string;
	token?: string;
	/** Plain http, for a local Content Hub. */
	insecure?: boolean;
	/** CMS opened by the inspector "Edit" buttons. */
	cmsUrl?: string;
}

/** {@link ICpaConnectionOverride} without the token. */
export type TCpaConnectionInfo = Omit<ICpaConnectionOverride, 'token'> & {
	/** The override carries its own token. */
	hasToken?: boolean;
};

/** Where and how to call a Content Hub API (always its v2 API: the storefront reads the v2 format only). */
export interface IContentHubConnection {
	host: string;
	spaceId: string;
	environmentId: string;
	/** Plain http, for a local Content Hub. */
	insecure: boolean;
	/** Bearer token, server side only. */
	token?: string;
}

/** CPA connection resolved by the server for a request. */
export interface ICpaConnection extends IContentHubConnection {
	/** True when the values come from a valid override. */
	overridden: boolean;
}

/** `app.cmsAPI.parameters` read here. */
interface ICmsParameters {
	spaceId?: string;
	environmentId?: string;
}

/**
 * Connection of the instance: env vars (MRT environment, or `config/local*.js` locally) over the app config.
 * Tokens only come from env vars: `APP_CDA_TOKEN`, `APP_PREVIEW_TOKEN`.
 * @param {boolean} preview - Preview API (CPA) instead of the delivery one (CDA)
 * @param {ICmsParameters} params - `app.cmsAPI.parameters`
 * @param {Record<string, string | undefined>} env - `process.env` on the server, `{}` in the browser
 * @returns {IContentHubConnection}
 */
export const getDefaultContentHubConnection = (
	preview: boolean,
	params: ICmsParameters,
	env: Record<string, string | undefined>,
): IContentHubConnection => ({
	host: (preview ? env.CPA_HOST : env.CDA_HOST) || (preview ? DEFAULT_CPA_HOST : DEFAULT_CDA_HOST),
	spaceId: env.CMS_SPACE || params.spaceId,
	environmentId: env.CMS_ENV || params.environmentId,
	insecure: env.CMS_INSECURE === 'true',
	token: preview ? env.APP_PREVIEW_TOKEN : env.APP_CDA_TOKEN,
});

/**
 * Base URL of the space environment on the v2 API: `{protocol}://{host}/v2/spaces/{space}/environments/{env}`.
 * @param {IContentHubConnection} connection - Connection
 * @returns {string}
 */
export const getContentHubBaseUrl = (connection: IContentHubConnection): string =>
	`${connection.insecure ? 'http' : 'https'}://${connection.host}/v2/spaces/${connection.spaceId}/environments/${connection.environmentId}`;

const HOST_REGEX = /^[a-z0-9.-]+(:\d{1,5})?$/i;
const ID_REGEX = /^[\w-]{1,128}$/;
const TOKEN_REGEX = /^[\w.~+/=-]{1,512}$/;

/**
 * Validates the override values one by one.
 * @param {unknown} value - Parsed JSON
 * @returns {{override: ICpaConnectionOverride | null, errors: string[]}} Normalised override (empty fields
 * dropped) and the names of the invalid fields
 */
export const validateCpaConnectionOverride = (value: unknown): {override: ICpaConnectionOverride | null; errors: string[]} => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return {override: null, errors: ['body']};
	}

	const input = value as Record<string, unknown>;
	const override: ICpaConnectionOverride = {};
	const errors: string[] = [];

	const text = (key: string): string | undefined => {
		const v = input[key];

		return typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined;
	};

	const host = text('host');

	if (host !== undefined) {
		HOST_REGEX.test(host) ? (override.host = host.toLowerCase()) : errors.push('host');
	}

	(['spaceId', 'environmentId'] as const).forEach((key) => {
		const v = text(key);

		if (v !== undefined) {
			ID_REGEX.test(v) ? (override[key] = v) : errors.push(key);
		}
	});

	const token = text('token');

	if (token !== undefined) {
		TOKEN_REGEX.test(token) ? (override.token = token) : errors.push('token');
	}

	if (input.insecure === true) {
		override.insecure = true;
	} else if (input.insecure !== undefined && input.insecure !== false) {
		errors.push('insecure');
	}

	const cmsUrl = text('cmsUrl');

	if (cmsUrl !== undefined) {
		try {
			const url = new URL(cmsUrl);

			['http:', 'https:'].includes(url.protocol) ? (override.cmsUrl = url.origin) : errors.push('cmsUrl');
		} catch {
			errors.push('cmsUrl');
		}
	}

	return {override: errors.length === 0 ? override : null, errors};
};

/**
 * Whether `host` (`hostname[:port]`) matches one of the patterns: `*` stands for any run of characters, so
 * `*.contentsvc.com` matches the subdomains and `localhost:*` any port.
 * @param {string} host - Host to check
 * @param {string[]} patterns - Allowed hosts
 * @returns {boolean}
 */
export const matchesHostPattern = (host: string, patterns: string[]): boolean =>
	patterns.some((pattern) => {
		const regex = new RegExp(`^${pattern.split('*').map((p) => p.replace(/[.+?^${}()|[\]\\]/g, '\\$&')).join('.*')}$`, 'i');

		return regex.test(host);
	});

/**
 * Whether the host is a loopback one, the only hosts reachable over plain http.
 * @param {string} host - `hostname[:port]`
 * @returns {boolean}
 */
export const isLoopbackHost = (host: string): boolean => matchesHostPattern(host, ['localhost', 'localhost:*', '127.0.0.1', '127.0.0.1:*']);

/**
 * Reads the info cookie value (JSON) written by the server.
 * @param {string | undefined} raw - Cookie value, already URI-decoded
 * @returns {TCpaConnectionInfo | null}
 */
export const parseCpaConnectionInfo = (raw: string | undefined): TCpaConnectionInfo | null => {
	if (!raw) {
		return null;
	}

	try {
		const value = JSON.parse(raw);
		const {override} = validateCpaConnectionOverride(value);

		if (!override) {
			return null;
		}

		delete override.token;

		return {...override, hasToken: value.hasToken === true};
	} catch {
		return null;
	}
};
